import React from 'react';
import IconButton from '@mui/material/IconButton';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import Tooltip from '@mui/material/Tooltip';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';

interface SpeechButtonProps {
  onResult?: (text: string) => void;
  onPartialResult?: (text: string) => void;
  onStartListening?: () => void;
  onClearTranscript?: (clearTranscript: () => void) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  sx?: any;
}

const SpeechButton: React.FC<SpeechButtonProps> = (props) => {
  const {
    onResult,
    onPartialResult,
    onStartListening,
    onClearTranscript,
    disabled = false,
    size = 'medium',
    sx = {}
  } = props;
  
  const {
    isListening,
    isSupported,
    toggleListening,
    clearTranscript
  } = useSpeechRecognition({
    onResult,
    onPartialResult
  });

  // Expose clearTranscript function to parent component
  React.useEffect(() => {
    if (onClearTranscript) {
      onClearTranscript(clearTranscript);
    }
  }, [onClearTranscript, clearTranscript]);

  const isDisabled = disabled || !isSupported;

  const handleClick = () => {
    if (!isDisabled) {
      // Call onStartListening when starting to listen
      if (!isListening && onStartListening) {
        onStartListening();
      }
      toggleListening();
    }
  };

  const getTooltipText = () => {
    if (!isSupported) {
      return 'Speech recognition not available';
    }
    if (disabled) {
      return 'Speech input disabled';
    }
    return isListening ? 'Stop listening' : 'Start voice input';
  };

  return (
    <Tooltip title={getTooltipText()} arrow>
      <span>
        <IconButton
          onClick={handleClick}
          color={isListening ? 'error' : 'primary'}
          disabled={isDisabled}
          size={size}
          sx={sx}
        >
          {isListening ? <MicOffIcon /> : <MicIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default SpeechButton; 